import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedService } from '@services/shared.service';
import {
  CompanionService,
  WhatsappConversationLogQuery,
} from '@services/companion.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

export type EmojiCategory = 'smileys' | 'hands' | 'hearts' | 'objects';

@Component({
  selector: 'app-whatsapp-conversation-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzToolTipModule,
    NzDrawerModule,
    NzModalModule,
    NzSelectModule,
    NzDatePickerModule,
    NzTagModule,
    NzInputModule,
    NzTabsModule,
    TranslateModule,
  ],
  templateUrl: './whatsapp-conversation-log.component.html',
  styleUrls: ['./whatsapp-conversation-log.component.scss'],
})
export class WhatsappConversationLogComponent implements OnInit, OnDestroy {
  private companion = inject(CompanionService);
  private sharedService = inject(SharedService);
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);

  // Selected phone thread for interactive chat view
  activeChatPhone: string | null = null;

  // New message input text
  newMessageText = '';

  // Emoji & Attachment popups state
  emojiPickerVisible = false;
  attachmentMenuVisible = false;
  emojiCategory: EmojiCategory = 'smileys';
  
  // Active mode: 'chat' | 'tokens'
  reportType: 'chat' | 'tokens' = 'chat';

  // Log rows and pagination state
  items: any[] = [];
  totalItems = 0;
  loading = false;
  currentPage = 1;
  pageSize = 25;

  // Filter Payload State (maps 1-to-1 with endpoint payload)
  searchTerm = '';
  phoneFilter = '';
  companyIdFilter: number | null = null;
  userIdFilter: number | null = null;
  directionFilter = '';
  intentFilter = '';
  categoryFilter = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  // View Mode: Modern WhatsApp Chat UI vs Data Table
  viewMode: 'chat' | 'table' = 'chat';

  emojiSearchTerm = '';

  emojiCategories: { id: EmojiCategory; icon: string; label: string }[] = [
    { id: 'smileys', icon: '😀', label: 'Smileys' },
    { id: 'hands', icon: '👍', label: 'Hands' },
    { id: 'hearts', icon: '❤️', label: 'Hearts' },
    { id: 'objects', icon: '🎯', label: 'Objects' },
  ];

  emojiListMap: Record<string, string[]> = {
    smileys: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋',
      '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐',
      '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯',
      '😴', '😌', '😎', '🤓', '🧐', '🥳', '🤠', '😲', '😳', '🥺',
      '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣',
      '😞', '😓', '😩', '😫', '😮‍💨', '😤', '😡', '😠', '🤬', '😈',
      '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾'
    ],
    hands: [
      '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙',
      '👈', '👉', '👆', '🖕', '👇', '☝️', '🖐️', '✋', '🖖', '👋',
      '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪'
    ],
    hearts: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '✨',
      '🌟', '⭐', '💫', '💥', '🔥', '💧', '💨', '☀️', '🌙', '⚡'
    ],
    objects: [
      '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️',
      '🎫', '🎟️', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹',
      '📱', '📲', '💻', '⌨️', '🖥️', '📸', '📹', '🔔', '💡', '💰',
      '💳', '💎', '🚀', '🎯', '✅', '💯', '📩', '📦', '🔮', '🔑'
    ]
  };

  getFilteredEmojis(): string[] {
    const list = this.emojiListMap[this.emojiCategory] || [];
    if (this.emojiSearchTerm.trim()) {
      const term = this.emojiSearchTerm.toLowerCase();
      return list.filter((e) => e.includes(term));
    }
    return list;
  }

  private loadEmojisFromApi(): void {
    const emojiApiUrl = 'https://raw.githubusercontent.com/github/gemoji/master/db/emoji.json';
    this.http.get<any[]>(emojiApiUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (Array.isArray(data) && data.length > 0) {
          const smileys: string[] = [];
          const hands: string[] = [];
          const hearts: string[] = [];
          const objects: string[] = [];

          data.forEach((item) => {
            if (!item.emoji) return;
            const category = (item.category || '').toLowerCase();
            const desc = (item.description || '').toLowerCase();
            const tags = Array.isArray(item.tags) ? item.tags.join(' ') : '';
            const aliases = Array.isArray(item.aliases) ? item.aliases.join(' ') : '';
            const allText = `${desc} ${tags} ${aliases}`;

            if (category.includes('smile') || category.includes('emotion') || category.includes('people')) {
              if (allText.includes('hand') || allText.includes('finger') || allText.includes('thumb') || allText.includes('clap') || allText.includes('fist')) {
                hands.push(item.emoji);
              } else {
                smileys.push(item.emoji);
              }
            } else if (category.includes('symbol') || category.includes('flag') || allText.includes('heart') || allText.includes('love') || allText.includes('star')) {
              hearts.push(item.emoji);
            } else {
              objects.push(item.emoji);
            }
          });

          if (smileys.length) this.emojiListMap['smileys'] = smileys;
          if (hands.length) this.emojiListMap['hands'] = hands;
          if (hearts.length) this.emojiListMap['hearts'] = hearts;
          if (objects.length) this.emojiListMap['objects'] = objects;
        }
      },
      error: (err) => {
        console.warn('Emoji API load fallback to default list:', err);
      }
    });
  }

  // Filter Drawer State
  filterDrawerVisible = false;

  // Detail inspection drawer
  drawerVisible = false;
  selectedItem: any | null = null;
  rawEntries: { key: string; value: string }[] = [];

  // Summary Metrics
  summaryMetrics = {
    totalLogs: 0,
    inboundCount: 0,
    outboundCount: 0,
    totalTokens: 0,
    activePhones: 0,
  };

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private fetchSub?: Subscription;

  ngOnInit(): void {
    this.loadReadThreads();
    this.loadEmojisFromApi();

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 1;
        this.fetchData(1, false);
      });

    this.fetchData(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onReportTypeChange(type: 'chat' | 'tokens'): void {
    const view = type === 'tokens' ? 'table' : 'chat';
    if (this.reportType === type && this.viewMode === view) return;
    this.reportType = type;
    this.viewMode = view;
    this.currentPage = 1;
    this.activeChatPhone = null;
    // The other report's rows don't fit this one — clear them so the switch shows at once
    // instead of waiting on the request.
    this.items = [];
    this.totalItems = 0;
    this.calculateMetrics([]);
    this.fetchData(1);
  }

  fetchData(page: number = 1, showLoader: boolean = true): void {
    // A slower, older request (e.g. the chat report) must not land on top of the one just asked for.
    this.fetchSub?.unsubscribe();
    if (showLoader) this.sharedService.showLoader();
    this.loading = true;

    const query: WhatsappConversationLogQuery = {
      report: this.reportType,
      search: this.searchTerm?.trim() || '',
      phone: this.phoneFilter?.trim() || '',
      company_id: this.companyIdFilter ? Number(this.companyIdFilter) : 0,
      user_id: this.userIdFilter ? Number(this.userIdFilter) : 0,
      direction: this.directionFilter || '',
      intent: this.intentFilter?.trim() || '',
      category: this.categoryFilter?.trim() || '',
      date_from: this.dateFrom ? this.formatDate(this.dateFrom) : '',
      date_to: this.dateTo ? this.formatDate(this.dateTo) : '',
      page: page,
      page_size: this.pageSize,
    };

    this.fetchSub = this.companion
      .getWhatsappConversationLog(query)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Also runs when a newer request cancels this one, so the loader never sticks.
          this.loading = false;
          if (showLoader) this.sharedService.hideLoader();
        })
      )
      .subscribe({
        next: (result) => {
          const rows = result.items || [];
          this.items = rows;
          this.totalItems = result.total || rows.length;
          this.currentPage = result.page;
          this.pageSize = result.pageSize;
          this.calculateMetrics(this.items);
          if (!this.activeChatPhone) {
            const threads = this.getGroupedPhoneThreads();
            if (threads.length > 0) this.activeChatPhone = threads[0].phone;
          }
        },
        error: (err: any) => {
          // No made-up rows here: the old sample fallback showed other companies' chats.
          this.items = [];
          this.totalItems = 0;
          this.calculateMetrics([]);
          this.sharedService.createNotification(
            'error',
            err?.error?.message || this.translateOrFallback('whatsapp-conversation.load-failed', 'Failed to load WhatsApp conversation logs')
          );
        },
      });
  }

  private calculateMetrics(rows: any[]): void {
    let inbound = 0;
    let outbound = 0;
    let tokens = 0;
    const phoneSet = new Set<string>();

    rows.forEach((row) => {
      const dir = (row.direction || row.flow || '').toLowerCase();
      if (dir.includes('in')) inbound++;
      else if (dir.includes('out')) outbound++;

      const phone = row.phone || row.phone_number || row.whatsapp_number;
      if (phone) phoneSet.add(phone);

      const tok = Number(row.tokens_used || row.total_tokens || row.tokens || 0);
      if (!isNaN(tok)) tokens += tok;
    });

    this.summaryMetrics = {
      totalLogs: this.totalItems,
      inboundCount: inbound,
      outboundCount: outbound,
      totalTokens: tokens,
      activePhones: phoneSet.size,
    };
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.fetchData(1);
  }

  onPageIndexChange(index: number): void {
    this.currentPage = index;
    this.fetchData(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.fetchData(1);
  }

  openFilterDrawer(): void {
    this.filterDrawerVisible = true;
  }

  closeFilterDrawer(): void {
    this.filterDrawerVisible = false;
  }

  openFilterModal(): void {
    this.filterDrawerVisible = true;
  }

  closeFilterModal(): void {
    this.filterDrawerVisible = false;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.fetchData(1);
    this.closeFilterDrawer();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.phoneFilter = '';
    this.companyIdFilter = null;
    this.userIdFilter = null;
    this.directionFilter = '';
    this.intentFilter = '';
    this.categoryFilter = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.currentPage = 1;
    this.activeChatPhone = null;
    this.fetchData(1);
  }

  hasActiveFilters(): boolean {
    return !!(
      this.searchTerm ||
      this.phoneFilter ||
      this.companyIdFilter ||
      this.userIdFilter ||
      this.directionFilter ||
      this.intentFilter ||
      this.categoryFilter ||
      this.dateFrom ||
      this.dateTo
    );
  }

  threadSearchTerm = '';

  // ── Phone Chat Thread View ──────────────────────────────────────────────────
  getGroupedPhoneThreads(): { phone: string; companyId: any; lastMessage: string; lastTime: any; count: number; messages: any[] }[] {
    const map = new Map<string, any[]>();
    for (const item of this.items) {
      const phone = this.getPhone(item);
      if (phone === '-') continue;
      if (!map.has(phone)) {
        map.set(phone, []);
      }
      map.get(phone)!.push(item);
    }

    let threads: any[] = [];
    map.forEach((msgs, phone) => {
      msgs.sort((a, b) => (a.created_at || a.timestamp || 0) - (b.created_at || b.timestamp || 0));
      const lastMsg = msgs[msgs.length - 1];
      threads.push({
        phone,
        companyId: this.getCompanyId(lastMsg),
        lastMessage: this.getMessageText(lastMsg),
        lastTime: lastMsg.created_at || lastMsg.timestamp,
        count: msgs.length,
        messages: msgs
      });
    });

    const sidebarQuery = (this.threadSearchTerm || '').trim().toLowerCase();
    const topQuery = (this.searchTerm || '').trim().toLowerCase();

    if (sidebarQuery) {
      threads = threads.filter((t) =>
        t.phone.toLowerCase().includes(sidebarQuery) ||
        (t.lastMessage || '').toLowerCase().includes(sidebarQuery) ||
        String(t.companyId).toLowerCase().includes(sidebarQuery)
      );
    }

    if (topQuery) {
      threads = threads.filter((t) =>
        t.phone.toLowerCase().includes(topQuery) ||
        (t.lastMessage || '').toLowerCase().includes(topQuery) ||
        String(t.companyId).toLowerCase().includes(topQuery) ||
        t.messages.some((m: any) => this.getMessageText(m).toLowerCase().includes(topQuery))
      );
    }

    return threads;
  }

  getAvailablePhoneNumbers(): string[] {
    const set = new Set<string>();
    for (const item of this.items) {
      const phone = this.getPhone(item);
      if (phone && phone !== '-') {
        set.add(phone);
      }
    }
    return Array.from(set);
  }

  readPhoneThreads = new Set<string>();

  private loadReadThreads(): void {
    try {
      const saved = localStorage.getItem('whatsapp_read_threads');
      if (saved) {
        const list: string[] = JSON.parse(saved);
        if (Array.isArray(list)) {
          this.readPhoneThreads = new Set(list);
        }
      }
    } catch (e) {
      console.warn('Could not load read threads from localStorage:', e);
    }
  }

  private saveReadThreads(): void {
    try {
      localStorage.setItem('whatsapp_read_threads', JSON.stringify(Array.from(this.readPhoneThreads)));
    } catch (e) {
      console.warn('Could not save read threads to localStorage:', e);
    }
  }

  getActiveThreadMessages(): any[] {
    if (!this.activeChatPhone) {
      const threads = this.getGroupedPhoneThreads();
      if (threads.length > 0) {
        this.activeChatPhone = threads[0].phone;
        this.readPhoneThreads.add(threads[0].phone);
        this.saveReadThreads();
      } else {
        return [];
      }
    } else {
      this.readPhoneThreads.add(this.activeChatPhone);
      this.saveReadThreads();
    }
    const msgs = this.items.filter(x => this.getPhone(x) === this.activeChatPhone);
    return msgs.sort((a, b) => (a.created_at || a.timestamp || 0) - (b.created_at || b.timestamp || 0));
  }

  selectPhoneThread(phone: string): void {
    this.activeChatPhone = phone;
    this.readPhoneThreads.add(phone);
    this.saveReadThreads();
  }

  isThreadRead(phone: string): boolean {
    return this.readPhoneThreads.has(phone) || this.activeChatPhone === phone;
  }

  getSelectedThreadCompanyId(): string | number {
    const threads = this.getGroupedPhoneThreads();
    const selected = threads.find((t) => t.phone === this.activeChatPhone);
    return selected ? selected.companyId : (threads.length > 0 ? threads[0].companyId : '-');
  }

  getSenderName(msg: any): string {
    if (!msg) return 'Participant';
    if (msg.user_name) return msg.user_name;
    if (msg.sender_name) return msg.sender_name;
    if (msg.name) return msg.name;
    const dir = this.getDirection(msg).toLowerCase();
    if (dir.includes('in')) {
      return 'Luqta Participant';
    } else {
      return 'Luqta Assistant';
    }
  }

  getSenderInitials(msg: any): string {
    const name = this.getSenderName(msg).replace(/^\+/, '').trim();
    if (!name) return 'U';
    const parts = name.split(/\s+/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (name.length >= 2) {
      return name.substring(0, 2).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  toggleEmojiPicker(): void {
    this.emojiPickerVisible = !this.emojiPickerVisible;
    if (this.emojiPickerVisible) this.attachmentMenuVisible = false;
  }

  addEmoji(emoji: string): void {
    this.newMessageText += emoji;
    this.emojiPickerVisible = false;
  }

  toggleAttachmentMenu(): void {
    this.attachmentMenuVisible = !this.attachmentMenuVisible;
    if (this.attachmentMenuVisible) this.emojiPickerVisible = false;
  }

  sendQuickAttachment(type: 'image' | 'document' | 'code'): void {
    if (!this.activeChatPhone) return;
    this.attachmentMenuVisible = false;

    let body = '';
    let intent = '';
    let category = '';

    if (type === 'image') {
      body = '📷 Sent campaign entry receipt image: receipt_scan_882.jpg';
      intent = 'image_upload';
      category = 'contest_entry';
    } else if (type === 'document') {
      body = '📄 Submitted claim document: voucher_claim_form.pdf';
      intent = 'document_claim';
      category = 'reward_payout';
    } else {
      body = '🎫 Submitted Campaign Voucher Code: #LQT-7749';
      intent = 'voucher_code';
      category = 'instant_reward';
    }

    const userMsg = {
      id: Date.now(),
      created_at: Date.now(),
      phone: this.activeChatPhone,
      company_id: this.getSelectedThreadCompanyId(),
      direction: 'inbound',
      intent: intent,
      category: category,
      body: body,
      tokens_used: 25,
    };

    this.items = [...this.items, userMsg];

    setTimeout(() => {
      let botReplyText = '';
      if (type === 'image') {
        botReplyText = 'Receipt verified successfully! Your lucky draw entry has been approved.';
      } else if (type === 'document') {
        botReplyText = 'Document received and validated. Your reward payout will be credited shortly.';
      } else {
        botReplyText = 'Voucher code #LQT-7749 is valid! You won $50 Gift Card.';
      }

      const botMsg = {
        id: Date.now() + 1,
        created_at: Date.now(),
        phone: this.activeChatPhone!,
        company_id: this.getSelectedThreadCompanyId(),
        direction: 'outbound',
        intent: 'bot_reply',
        category: 'ai_response',
        body: botReplyText,
        tokens_used: 50,
      };

      this.items = [...this.items, botMsg];
      this.summaryMetrics.totalLogs += 2;
      this.summaryMetrics.totalTokens += 75;
    }, 1000);
  }

  sendSimulatedMessage(): void {
    if (!this.newMessageText || !this.newMessageText.trim() || !this.activeChatPhone) return;
    this.emojiPickerVisible = false;
    this.attachmentMenuVisible = false;
    const textSent = this.newMessageText.trim();
    const userMsg = {
      id: Date.now(),
      created_at: Date.now(),
      phone: this.activeChatPhone,
      company_id: this.getSelectedThreadCompanyId(),
      direction: 'inbound',
      intent: 'live_chat',
      category: 'user_message',
      body: textSent,
      tokens_used: 15,
    };
    this.items = [...this.items, userMsg];
    this.newMessageText = '';

    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        created_at: Date.now(),
        phone: this.activeChatPhone!,
        company_id: this.getSelectedThreadCompanyId(),
        direction: 'outbound',
        intent: 'bot_reply',
        category: 'ai_response',
        body: `Thank you for messaging! Your request regarding "${textSent}" has been received and processed by Luqta WhatsApp Bot.`,
        tokens_used: 45,
      };
      this.items = [...this.items, botMsg];
      this.summaryMetrics.totalLogs += 2;
      this.summaryMetrics.totalTokens += 60;
    }, 1000);
  }

  openPhoneChatThread(phone: string): void {
    if (!phone) return;
    this.activeChatPhone = phone;
    this.viewMode = 'chat';
  }

  closePhoneChatThread(): void {
    this.activeChatPhone = null;
  }

  // ── Detail Drawer ─────────────────────────────────────────────────────────
  openDetailDrawer(item: any): void {
    this.selectedItem = item;
    this.rawEntries = this.buildRawEntries(item);
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedItem = null;
    this.rawEntries = [];
  }

  private buildRawEntries(raw: any): { key: string; value: string }[] {
    if (!raw || typeof raw !== 'object') return [];
    return Object.keys(raw).map((key) => {
      const value = raw[key];
      if (value === null || value === undefined || value === '') {
        return { key, value: '-' };
      }
      if (typeof value === 'object') {
        return { key, value: JSON.stringify(value, null, 2) };
      }
      return { key, value: String(value) };
    });
  }

  // ── Field Extraction & Formatting Helpers ─────────────────────────────────
  getPhone(row: any): string {
    return row.phone || row.phone_number || row.whatsapp_number || row.msisdn || '-';
  }

  getMessageText(row: any): string {
    return (
      row.body ||
      row.message ||
      row.message_text ||
      row.text ||
      row.description ||
      '-'
    );
  }

  getDirection(row: any): string {
    return row.direction || row.flow || 'inbound';
  }

  getDirectionClass(direction: string): string {
    const value = (direction || '').toLowerCase();
    if (value.includes('in')) return 'direction-in';
    if (value.includes('out')) return 'direction-out';
    return 'direction-default';
  }

  getCompanyId(row: any): string | number {
    return row.company_id ?? row.companyId ?? row.company?.id ?? '-';
  }

  getUserId(row: any): string | number {
    return row.user_id ?? row.userId ?? row.user?.id ?? '-';
  }

  getTokens(row: any): number {
    return Number(row.tokens_used || row.total_tokens || row.tokens || 0);
  }

  formatDateTime(timestamp: any): string {
    if (!timestamp) return '-';
    const date = new Date(typeof timestamp === 'number' ? timestamp : String(timestamp));
    if (isNaN(date.getTime())) return String(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated === key ? fallback : translated;
  }

  getPageRangeText(): string {
    const start = this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return `${start}-${end} of ${this.totalItems} items`;
  }
}
